import {
  loginOperadorRespostaSchema,
  operadorPode,
  type SessaoOperador,
} from '@construtora/contracts';
import { useSyncExternalStore } from 'react';
import { definirAccessToken, requisitar } from './api.ts';

/**
 * Sessão do console de operação.
 *
 * Store **separado** do de cliente, e não um campo a mais naquele. Os dois
 * planos podem estar abertos na mesma máquina, e um objeto único obrigaria
 * toda leitura a perguntar "de quem é esta sessão?" — pergunta que, esquecida
 * uma vez, mostra dados de operação para um usuário de cliente.
 *
 * Como no outro plano: access token em memória, refresh em cookie HttpOnly com
 * caminho próprio, nada em `localStorage` (.spec/005, seção 2).
 */

let sessao: SessaoOperador | null = null;
const ouvintes = new Set<() => void>();

export function obterSessaoOperador(): SessaoOperador | null {
  return sessao;
}

export function definirSessaoOperador(nova: SessaoOperador | null): void {
  if (sessao === nova) return;

  sessao = nova;
  for (const ouvinte of ouvintes) ouvinte();
}

function inscrever(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

export function useSessaoOperador(): SessaoOperador | null {
  return useSyncExternalStore(inscrever, obterSessaoOperador, obterSessaoOperador);
}

/** Checagem de conveniência para a interface. Quem autoriza é a API. */
export function operadorPodeFazer(permissao: string): boolean {
  const atual = obterSessaoOperador();
  return atual ? operadorPode(atual.perfil, permissao) : false;
}

export function useOperadorPode(permissao: string): boolean {
  useSessaoOperador();
  return operadorPodeFazer(permissao);
}

interface RespostaSessao {
  accessToken: string;
  operador: SessaoOperador;
}

export async function restaurarSessaoOperador(): Promise<void> {
  try {
    const dados = await requisitar<RespostaSessao>('/api/v1/operacao/auth/refresh', {
      method: 'POST',
    });
    definirAccessToken(dados.accessToken, 'OPERADOR');
    definirSessaoOperador(dados.operador);
  } catch {
    definirAccessToken(null, 'OPERADOR');
    definirSessaoOperador(null);
  }
}

export function encerrarSessaoOperadorLocal(): void {
  definirAccessToken(null, 'OPERADOR');
  definirSessaoOperador(null);
}

const operadorSchema = loginOperadorRespostaSchema.shape.operador;

/**
 * De quem é o token de operador que acabou de ser renovado?
 *
 * Igual à do outro plano, e pelo mesmo motivo — ver `conferirIdentidadeRenovada`
 * em `sessao.ts` para o raciocínio inteiro. O cookie de refresh do console tem
 * caminho próprio, mas continua sendo **do navegador**, não da aba: dois
 * operadores no mesmo Chrome caem exatamente no mesmo defeito.
 *
 * Aqui o risco é menor porque dois operadores na mesma máquina é situação mais
 * rara do que duas contas de cliente — o console é ferramenta interna. Menor não
 * é nenhum: quem opera o console suspende conta e provisiona empresa, e fazer
 * isso com o nome de outro operador no cabeçalho é pior, não melhor, do que no
 * plano do cliente.
 */
export function conferirIdentidadeOperadorRenovada(identidade: unknown): 'mesma' | 'outra' {
  const analise = operadorSchema.safeParse(identidade);
  if (!analise.success) return 'mesma';

  const atual = obterSessaoOperador();
  if (!atual) return 'mesma';
  if (atual.id !== analise.data.id) return 'outra';

  definirSessaoOperador(analise.data);
  return 'mesma';
}
